import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Student, FilePlus, ClockCountdown, CheckCircle, Plus, ChatCircle,
  Lock, WarningCircle, MagnifyingGlass, ArrowRight, Buildings,
  CalendarBlank, TrendUp, Sparkle, DotsThreeVertical, Megaphone, X, EnvelopeOpen,
} from '@phosphor-icons/react';

import { API } from '@/config/api';
import SubAgentsCard from '@/components/SubAgentsCard';
import NotificationBell from '@/components/NotificationBell';
import IntakeAccordion, { GroupByIntakeToggle, useGroupByIntake } from '@/components/IntakeAccordion';
import PreAuthorizationModal from '@/components/PreAuthorizationModal';
import SelfCompletePreAuthModal from '@/components/SelfCompletePreAuthModal';
import { ShieldCheck } from '@phosphor-icons/react';

/* --------------------------------------------------------------------------
   Pipeline stage config — kept in sync with admin Dashboard.js. Recruiters
   see this read-only.
   -------------------------------------------------------------------------- */
const PIPELINE_LABELS = {
  profile_created: 'Profile Created',
  application_sent: 'Application Sent',
  docs_under_review: 'Docs Under Review',
  docs_verified: 'Docs Verified',
  enrollment_agreement_sent: 'EA Sent',
  enrollment_agreement_signed: 'EA Signed',
  sabc_in_process: 'SABC In Process',
  coe_sent: 'COE Sent',
  half_money_received: '1/2 Money Received',
  second_money_received: '2/2 Money Received',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};
const PIPELINE_COLORS = {
  profile_created: 'bg-blue-100 text-blue-700 border-blue-200',
  application_sent: 'bg-purple-100 text-purple-700 border-purple-200',
  docs_under_review: 'bg-amber-100 text-amber-700 border-amber-200',
  docs_verified: 'bg-teal-100 text-teal-800 border-teal-300',
  enrollment_agreement_sent: 'bg-amber-100 text-amber-800 border-amber-200',
  enrollment_agreement_signed: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  sabc_in_process: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
  coe_sent: 'bg-blue-100 text-blue-800 border-blue-300',
  half_money_received: 'bg-lime-100 text-lime-800 border-lime-300',
  second_money_received: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  withdrawn: 'bg-slate-100 text-slate-700 border-slate-300',
};
const PIPELINE_DOTS = {
  profile_created: 'bg-blue-500',
  application_sent: 'bg-purple-500',
  docs_under_review: 'bg-amber-500',
  docs_verified: 'bg-teal-500',
  enrollment_agreement_sent: 'bg-amber-500',
  enrollment_agreement_signed: 'bg-yellow-500',
  sabc_in_process: 'bg-fuchsia-500',
  coe_sent: 'bg-blue-500',
  half_money_received: 'bg-lime-500',
  second_money_received: 'bg-emerald-500',
  rejected: 'bg-red-500',
  withdrawn: 'bg-slate-500',
};

// Deterministic avatar palette for student initials bubbles.
const AVATAR_PALETTE = [
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-violet-100 text-violet-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700',
];
function avatarForId(id = '') {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}
function initialsOf(name = '') {
  return (name.trim().split(/\s+/).map(p => p[0]).filter(Boolean).slice(0, 2).join('') || '?').toUpperCase();
}
function greetingFor(name = '') {
  const h = new Date().getHours();
  const greet = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  const first = (name || '').split(/\s+/)[0] || '';
  return `${greet}${first ? `, ${first}` : ''}`;
}

/* --------------------------------------------------------------------------
   Recruiter Dashboard — modern rebuild
   -------------------------------------------------------------------------- */
export default function RecruiterDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [commData, setCommData] = useState(null);
  const [tiersInfo, setTiersInfo] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [preAuthStudent, setPreAuthStudent] = useState(null);
  const [selfPreAuthStudent, setSelfPreAuthStudent] = useState(null);
  // Admin whitelists recruiters for the self-complete flow; the flag arrives
  // on /auth/me (see server.py) and switches the Pre-Auth row-action from
  // "email the student" to "sign on their behalf".
  const canSelfCompletePreAuth = !!user?.can_self_complete_pre_auth;
  const [missingDocs, setMissingDocs] = useState([]);
  const [resolvingFlag, setResolvingFlag] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [recGroupByIntake, setRecGroupByIntake] = useGroupByIntake('recruiter.students.groupByIntake', true);
  // Client-side pagination — 50 rows per group by default. The recruiter
  // portal loads every student in one API call (limit=5000), but rendering
  // 500+ rows in a single <tbody> stutters. Instead we cap what mounts and
  // show a "Load more" button that grows the cap in 50-row chunks.
  // Key = group identifier (intake_label for grouped mode, 'all' for flat).
  // Reset whenever filters change so page-1 is always page-1.
  const PAGE_SIZE = 50;
  const [visibleCounts, setVisibleCounts] = useState({});
  useEffect(() => { setVisibleCounts({}); }, [searchTerm, stageFilter, recGroupByIntake, students.length]);
  const getVisibleCount = (key) => visibleCounts[key] ?? PAGE_SIZE;
  const loadMore = (key) => setVisibleCounts(prev => ({ ...prev, [key]: (prev[key] ?? PAGE_SIZE) + PAGE_SIZE }));

  const loadMissingDocs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/recruiter/missing-docs`);
      setMissingDocs(Array.isArray(data) ? data : []);
    } catch { /* ignore — not critical */ }
  }, []);
  useEffect(() => { loadMissingDocs(); }, [loadMissingDocs]);

  const loadAnnouncements = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/recruiter/announcements/active`);
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch { /* ignore — not critical */ }
  }, []);
  useEffect(() => { loadAnnouncements(); }, [loadAnnouncements]);

  const dismissAnnouncement = async (annId) => {
    // Optimistic — hide it immediately; if the server 404s, refresh.
    setAnnouncements(prev => prev.filter(a => a.id !== annId));
    try {
      await axios.post(`${API}/recruiter/announcements/${annId}/dismiss`);
    } catch {
      await loadAnnouncements();
    }
  };

  // ----------------------------------------------------------------
  // College communications flash banner — surfaces every unread note
  // a college has left on any of this recruiter's students, right at
  // the top of the dashboard so nothing gets missed. Backend endpoint
  // filters ``notifications`` by ``kind IN institution_comment / flag
  // / redo / stage_change``.
  // ----------------------------------------------------------------
  const [collegeComms, setCollegeComms] = useState({ items: [], unread_count: 0 });
  const [collegeCommsDismissing, setCollegeCommsDismissing] = useState(false);
  const loadCollegeComms = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/recruiter/college-communications?limit=5`);
      setCollegeComms({ items: data?.items || [], unread_count: data?.unread_count || 0 });
    } catch { /* silent — banner just stays hidden */ }
  }, []);
  useEffect(() => { loadCollegeComms(); }, [loadCollegeComms]);

  const dismissCollegeComms = async () => {
    // Optimistic wipe — snap the banner shut, restore on server error.
    const snapshot = collegeComms;
    setCollegeComms({ items: [], unread_count: 0 });
    setCollegeCommsDismissing(true);
    try {
      await axios.post(`${API}/recruiter/college-communications/mark-all-read`);
      toast.success('Marked all college notes as read');
    } catch (err) {
      setCollegeComms(snapshot);
      toast.error(err?.response?.data?.detail || 'Failed to mark as read');
    } finally {
      setCollegeCommsDismissing(false);
    }
  };

  const submitResolve = async () => {
    if (!resolvingFlag) return;
    try {
      await axios.post(`${API}/recruiter/missing-docs/${resolvingFlag.id}/resolve`, { resolution_note: resolveNote });
      toast.success('Marked resolved — the college has been notified');
      setResolvingFlag(null); setResolveNote('');
      await loadMissingDocs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to resolve');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, stuRes, commRes, convRes, tiersRes, fcRes] = await Promise.all([
        axios.get(`${API}/recruiter/stats`),
        axios.get(`${API}/students?limit=5000`),
        axios.get(`${API}/recruiter/commissions`),
        axios.get(`${API}/messages/conversations`).catch(() => ({ data: [] })),
        axios.get(`${API}/recruiter/me/tiers`).catch(() => ({ data: { tiers: [], flat_commission: 0 } })),
        axios.get(`${API}/recruiter/earnings-forecast`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setStudents(stuRes.data);
      setCommData(commRes.data);
      setUnreadMessages(convRes.data?.[0]?.unread || 0);
      setTiersInfo(tiersRes.data);
      setForecast(fcRes.data);
    } catch (err) {
      console.error('Failed to fetch recruiter data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggleSABC = async (sId, currentValue) => {
    setTogglingId(sId);
    try {
      const res = await axios.patch(`${API}/students/${sId}/student-aid-bc`, { uploaded: !currentValue });
      setStudents(prev => prev.map(s => s.id === sId ? {
        ...s,
        student_aid_bc_uploaded: res.data.student_aid_bc_uploaded,
        student_aid_bc_uploaded_at: res.data.student_aid_bc_uploaded_at,
      } : s));
      toast.success(res.data.student_aid_bc_uploaded ? 'Marked uploaded on Student Aid BC' : 'Marked as not uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    } finally {
      setTogglingId(null);
    }
  };

  // Derived — filter/search over students.
  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return students.filter(s => {
      if (stageFilter !== 'all' && (s.application_stage || 'profile_created') !== stageFilter) return false;
      if (!q) return true;
      return (s.full_name || '').toLowerCase().includes(q)
        || (s.email || '').toLowerCase().includes(q)
        || (s.preferred_program || '').toLowerCase().includes(q)
        || (s.assigned_college_name || '').toLowerCase().includes(q);
    });
  }, [students, searchTerm, stageFilter]);

  // Stage counts over the full unfiltered list — powers the pill filter row.
  const stageCounts = useMemo(() => {
    const counts = { all: students.length };
    students.forEach(s => {
      const stg = s.application_stage || 'profile_created';
      counts[stg] = (counts[stg] || 0) + 1;
    });
    return counts;
  }, [students]);

  // Conversion % — from Profile Created → COE Sent (any post-COE stage counts).
  const conversionPct = useMemo(() => {
    if (!students.length) return 0;
    const coeStages = new Set(['coe_sent', 'half_money_received', 'second_money_received']);
    const coe = students.filter(s => coeStages.has(s.application_stage)).length;
    return Math.round((coe / students.length) * 100);
  }, [students]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="recruiter-dashboard-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin" />
          <p className="text-sm text-gray-500">Loading your portal…</p>
        </div>
      </div>
    );
  }

  const commissionsHidden = !!(commData?.hidden ?? tiersInfo?.hidden ?? forecast?.hidden) || !!user?.is_sub_agent;
  const isSubAgent = !!user?.is_sub_agent;

  return (
    <div className="min-h-screen bg-[#f8f7f4] text-gray-900" data-testid="recruiter-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-12">

        {/* Editorial header */}
        <header className="flex items-start justify-between gap-6" data-testid="recruiter-hero">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400">Recruiter Portal</p>
            <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight text-gray-900 mt-2 leading-[1.05]" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              {greetingFor(user?.name)}
            </h1>
            <p className="text-gray-500 text-[13px] mt-3 max-w-xl leading-relaxed">
              {students.length === 0
                ? "Ready to add your first student? Create a profile to get started."
                : `You're managing ${students.length} student${students.length === 1 ? '' : 's'}. Keep the momentum going.`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <NotificationBell />
            <Link to="/recruiter/messages" data-testid="recruiter-messages-link">
              <Button variant="outline" className="relative bg-white border-gray-200 hover:bg-gray-100 text-gray-800 h-10 rounded-md text-[12px] font-medium">
                <ChatCircle size={14} className="mr-2" /> Messages
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-gray-900 rounded-full" data-testid="recruiter-unread-badge">{unreadMessages}</span>
                )}
              </Button>
            </Link>
            <Link to="/recruiter/create-student" data-testid="create-student-btn">
              <Button className="bg-gray-900 hover:bg-black text-white h-10 rounded-md px-5 text-[12px] font-medium">
                <Plus size={14} className="mr-2" /> New Student
              </Button>
            </Link>
          </div>
        </header>

        {/* College Communications Flash Banner —
            Surfaces the newest 5 unread notes / flags a college left on
            any of the recruiter's students. Auto-hides when empty. */}
        {collegeComms.items.length > 0 && (
          <section
            data-testid="recruiter-college-comms-banner"
            className="relative rounded-2xl border-2 border-indigo-500/60 bg-gradient-to-br from-indigo-50 via-white to-purple-50/50 shadow-[0_10px_40px_-12px_rgba(79,70,229,0.35)] overflow-hidden animate-in fade-in slide-in-from-top-4 duration-500"
          >
            {/* Pulsing dot indicator */}
            <span className="absolute top-4 right-4 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-600"></span>
            </span>
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/40">
                    <EnvelopeOpen size={18} weight="fill" className="text-white" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-700">Colleges reached out</p>
                    <h3 className="text-[15px] sm:text-[17px] font-semibold text-gray-900 leading-tight mt-0.5" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                      {collegeComms.unread_count === 1
                        ? '1 new message about your student'
                        : `${collegeComms.unread_count} new messages about your students`}
                    </h3>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={dismissCollegeComms}
                  disabled={collegeCommsDismissing}
                  className="h-8 px-3 text-[11.5px] font-medium text-indigo-700 hover:bg-indigo-100 rounded-md"
                  data-testid="dismiss-college-comms-btn"
                >
                  {collegeCommsDismissing ? 'Dismissing…' : 'Mark all read'}
                </Button>
              </div>
              <ul className="mt-4 space-y-2.5">
                {collegeComms.items.slice(0, 5).map((n) => (
                  <li key={n.id} data-testid={`college-comm-${n.id}`}>
                    <Link
                      to={n.student_id ? `/recruiter/student/${n.student_id}` : '/recruiter'}
                      className="group flex items-start gap-3 p-3 rounded-lg bg-white/70 hover:bg-white border border-indigo-100 hover:border-indigo-300 transition-all"
                    >
                      <div className="w-8 h-8 rounded-md bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shrink-0 shadow-sm">
                        <Buildings size={13} weight="fill" className="text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-[12.5px] font-semibold text-gray-900 truncate">
                            {n.college_name || 'A college'} <span className="font-normal text-gray-500">·</span> <span className="font-medium text-indigo-700">{n.student_name || 'A student'}</span>
                          </p>
                          <span className="text-[10.5px] text-gray-500 shrink-0 tabular-nums">{n.created_at ? new Date(n.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}</span>
                        </div>
                        <p className="text-[12px] text-gray-700 mt-0.5 line-clamp-2 leading-snug">{n.message || n.title}</p>
                      </div>
                      <ArrowRight size={14} className="text-indigo-400 group-hover:text-indigo-700 group-hover:translate-x-0.5 transition mt-1.5 shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
              {collegeComms.unread_count > collegeComms.items.length && (
                <p className="text-[11px] text-indigo-700 mt-3 text-center font-medium">
                  + {collegeComms.unread_count - collegeComms.items.length} more — open the bell to see all
                </p>
              )}
            </div>
          </section>
        )}

        {/* KPI row moved below the Students List (see note further down). */}

        {/* Announcements — prominent news flash from admin, dismissible */}
        {announcements.length > 0 && (
          <section className="space-y-3" data-testid="recruiter-announcements">
            {announcements.map(a => (
              <article
                key={a.id}
                className="relative overflow-hidden border border-gray-900 bg-gray-900 text-white p-5 sm:p-6"
                data-testid={`announcement-${a.id}`}
              >
                <div className="flex items-start gap-4">
                  <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 border border-white/20">
                    <Megaphone size={14} weight="fill" className="text-white" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/60">Study Alliance · News</p>
                    <h3 className="text-xl font-semibold tracking-tight mt-1 leading-snug" style={{ fontFamily: "'Libre Baskerville', serif" }}>{a.subject}</h3>
                    <div className="text-[13px] text-white/80 mt-3 whitespace-pre-wrap leading-relaxed">{a.body}</div>
                    <p className="text-[10px] uppercase tracking-[0.14em] text-white/40 mt-4">
                      {new Date(a.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => dismissAnnouncement(a.id)}
                    className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60 hover:text-white transition-colors border border-white/20 hover:border-white/60 rounded-md px-3 py-1.5"
                    data-testid={`announcement-dismiss-${a.id}`}
                    title="Mark as read"
                  >
                    Got it
                  </button>
                </div>
              </article>
            ))}
          </section>
        )}

        {/* Missing-doc alerts from the college (only when any exist)      */}
        {missingDocs.length > 0 && (
          <section className="relative border-l-2 border-gray-900 bg-white pl-5 pr-5 py-5" data-testid="missing-docs-alert">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-8 h-8 rounded-full border border-gray-900/10 bg-gray-900/5 flex items-center justify-center">
                <WarningCircle size={14} weight="fill" className="text-gray-900" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[13px] font-semibold text-gray-900">
                  {missingDocs.length} document{missingDocs.length > 1 ? 's' : ''} flagged by the college — action required
                </h3>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Upload the missing document on the student profile, then mark it resolved.
                </p>
                <div className="mt-4 divide-y divide-gray-200 border-t border-gray-200">
                  {missingDocs.map(m => (
                    <div key={m.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between" data-testid={`missing-doc-row-${m.id}`}>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-gray-900">
                          {m.student_name}
                          <span className="text-[11px] font-normal text-gray-500 ml-2">· {m.college_name}</span>
                        </p>
                        <p className="text-[12px] text-gray-700 mt-0.5"><strong className="text-gray-900">Missing:</strong> {m.doc_type}</p>
                        {m.note && <p className="text-[11px] text-gray-500 mt-1 italic">&ldquo;{m.note}&rdquo;</p>}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Link to={`/recruiter/student/${m.student_id}`}>
                          <Button size="sm" variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-100 rounded-md text-[12px] font-medium" data-testid={`open-student-${m.student_id}`}>Open student</Button>
                        </Link>
                        <Button
                          size="sm"
                          className="bg-gray-900 hover:bg-black text-white rounded-md text-[12px] font-medium"
                          onClick={() => { setResolvingFlag(m); setResolveNote(''); }}
                          data-testid={`resolve-flag-${m.id}`}
                        >
                          Mark resolved
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* KPIs + Finance blocks moved below the Students List so the student
            table is the first thing a recruiter sees after login. */}

        {/* Students List — editorial */}
        <section className="bg-white border border-gray-200 overflow-hidden" data-testid="recruiter-students-list">
          <div className="px-6 lg:px-8 py-6 border-b border-gray-200 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">Pipeline</p>
              <h2 className="text-2xl font-semibold text-gray-900 mt-1" style={{ fontFamily: "'Libre Baskerville', serif" }}>Your Students</h2>
              <p className="text-[12px] text-gray-500 mt-1">
                <span className="tabular-nums font-semibold text-gray-900">{filteredStudents.length}</span> showing{filteredStudents.length !== students.length ? ` of ${students.length}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <GroupByIntakeToggle
                value={recGroupByIntake}
                onChange={setRecGroupByIntake}
                storageKey="recruiter.students.groupByIntake"
                testId="recruiter-group-by-intake-toggle"
              />
              <Link to="/recruiter/pre-authorization" data-testid="recruiter-preauth-prominent-link">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-10 rounded-md px-3 text-[12px] font-medium bg-white border-indigo-200 text-indigo-800 hover:bg-indigo-50"
                  title="Open the Pre-Authorization tab — review students ready for the Pre-Auth payment step"
                >
                  <ShieldCheck size={13} className="mr-1.5" /> Pre-Authorization
                </Button>
              </Link>
              <div className="relative">
                <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <Input
                  placeholder="Search name, email, program…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-10 w-64 bg-white border-gray-200 text-[13px]"
                  data-testid="student-search-input"
                />
              </div>
              <Link to="/recruiter/create-student">
                <Button size="sm" className="bg-gray-900 hover:bg-black text-white h-10 rounded-md px-4 text-[12px] font-medium">
                  <Plus size={13} className="mr-1.5" /> New Student
                </Button>
              </Link>
            </div>
          </div>

          {/* Stage pill filter row */}
          {students.length > 0 && (
            <div className="px-6 lg:px-8 py-4 border-b border-gray-200 flex flex-wrap gap-1.5 overflow-x-auto bg-gray-50/40" data-testid="stage-filter-pills">
              <StagePill active={stageFilter === 'all'} onClick={() => setStageFilter('all')} label="All" count={stageCounts.all || 0} tone="gray" testId="stage-pill-all" />
              {Object.keys(PIPELINE_LABELS).map(stg => (
                <StagePill
                  key={stg}
                  active={stageFilter === stg}
                  onClick={() => setStageFilter(stg)}
                  label={PIPELINE_LABELS[stg]}
                  count={stageCounts[stg] || 0}
                  dot={PIPELINE_DOTS[stg]}
                  testId={`stage-pill-${stg}`}
                />
              ))}
            </div>
          )}

          {students.length === 0 ? (
            <div className="p-16 text-center">
              <div className="w-14 h-14 mx-auto rounded-full border border-gray-200 bg-white flex items-center justify-center mb-5">
                <Sparkle size={22} className="text-gray-500" />
              </div>
              <p className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Let&rsquo;s start your pipeline</p>
              <p className="text-[13px] text-gray-500 mt-2 mb-6 max-w-sm mx-auto">Create your first student profile — you can save as a draft and finish later.</p>
              <Link to="/recruiter/create-student" data-testid="empty-create-student-btn">
                <Button className="bg-gray-900 hover:bg-black text-white rounded-md text-[12px] font-medium">
                  <Plus size={14} className="mr-2" /> Create your first student
                </Button>
              </Link>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="p-12 text-center">
              <Student size={28} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-[13px]">No students match your filter.</p>
              <Button variant="ghost" size="sm" className="mt-3 text-gray-700 hover:text-gray-900" onClick={() => { setSearchTerm(''); setStageFilter('all'); }}>
                Clear filter
              </Button>
            </div>
          ) : (() => {
            const renderTable = (items, groupKey = 'all') => {
              const visible = getVisibleCount(groupKey);
              const shown = items.slice(0, visible);
              const remaining = items.length - shown.length;
              return (
            <div className="overflow-x-auto" data-testid={`recruiter-students-table-${groupKey}`}>
              <table className="w-full">
                <thead className="bg-gradient-to-b from-gray-50 to-gray-50/60 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-5 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em]">Student</th>
                    <th className="text-left px-3 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] hidden md:table-cell w-[220px]">Program</th>
                    <th className="text-left px-3 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] hidden lg:table-cell w-[110px]">Intake</th>
                    <th className="text-left px-3 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] w-[170px]">Stage</th>
                    <th className="text-center px-3 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] w-[100px]" title="Have you uploaded this student's data on the Student Aid BC website?">SA BC</th>
                    <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] w-[110px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shown.map(s => {
                    const stg = s.application_stage || 'profile_created';
                    const stageCls = PIPELINE_COLORS[stg] || 'bg-gray-50 text-gray-600 border-gray-200';
                    const dotCls = PIPELINE_DOTS[stg] || 'bg-gray-300';
                    const avatar = avatarForId(s.id || '');
                    return (
                      <tr key={s.id} className={`hover:bg-blue-50/40 transition-colors ${s.open_missing_docs > 0 ? 'bg-amber-50/60' : ''}`} data-testid={`student-row-${s.id}`} data-missing-doc={s.open_missing_docs > 0 ? 'true' : 'false'}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            {s.open_missing_docs > 0 && (
                              <span className="w-1 self-stretch rounded-full bg-amber-500 animate-pulse" title="Missing document flagged by college" aria-hidden />
                            )}
                            <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-semibold ${avatar}`} aria-hidden>
                              {initialsOf(s.full_name)}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                                <Link to={`/recruiter/student/${s.id}`} className={`text-sm font-semibold hover:text-blue-700 transition-colors truncate block max-w-[220px] ${s.open_missing_docs > 0 ? 'text-amber-900' : 'text-gray-900'}`} title={s.full_name} data-testid={`student-name-${s.id}`}>
                                  {s.full_name}
                                </Link>
                                {s.open_missing_docs > 0 && (
                                  <span
                                    className="inline-flex items-center gap-0.5 shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-amber-100 text-amber-900 border border-amber-300 animate-pulse"
                                    title={`College flagged ${s.open_missing_docs} missing document${s.open_missing_docs === 1 ? '' : 's'} — please upload`}
                                    data-testid={`missing-doc-badge-${s.id}`}
                                  >
                                    <WarningCircle size={10} weight="fill" /> Missing doc{s.open_missing_docs > 1 ? ` ×${s.open_missing_docs}` : ''}
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-gray-500 truncate max-w-[220px]" title={s.email}>{s.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-xs hidden md:table-cell align-middle">
                          <p className="text-gray-800 truncate max-w-[200px]" title={s.preferred_program_name || s.preferred_program || ''}>{s.preferred_program_name || s.preferred_program || <span className="text-gray-300">—</span>}</p>
                          {s.assigned_college_name && (
                            <p className="text-[11px] text-blue-700 mt-0.5 truncate flex items-center gap-1 max-w-[200px]" title={s.assigned_college_name}>
                              <Buildings size={10} weight="fill" className="shrink-0" /> {s.assigned_college_name}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-3 text-[11px] hidden lg:table-cell align-middle whitespace-nowrap" data-testid={`student-intake-${s.id}`}>
                          {s.intake_label ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-800 border border-indigo-100 font-semibold">
                              <CalendarBlank size={10} weight="fill" /> {s.intake_label}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 align-middle" data-testid={`student-pipeline-${s.id}`}>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`inline-flex items-center gap-1.5 pl-2 pr-2 py-1 text-[11px] font-semibold rounded-full border ${stageCls}`} title="Admin-managed pipeline status">
                              <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`} />
                              {PIPELINE_LABELS[stg] || stg}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center align-middle">
                          <label className="inline-flex items-center gap-1.5 cursor-pointer select-none" title="Toggle when this student is uploaded on Student Aid BC">
                            <Checkbox
                              checked={!!s.student_aid_bc_uploaded}
                              disabled={togglingId === s.id}
                              onCheckedChange={() => handleToggleSABC(s.id, !!s.student_aid_bc_uploaded)}
                              data-testid={`sabc-toggle-${s.id}`}
                            />
                            <span className={`text-[11px] font-semibold ${s.student_aid_bc_uploaded ? 'text-emerald-700' : 'text-gray-400'}`} data-testid={`sabc-label-${s.id}`}>
                              {s.student_aid_bc_uploaded ? 'Yes' : 'No'}
                            </span>
                          </label>
                        </td>
                        <td className="px-4 py-3 text-right align-middle">
                          <div className="inline-flex items-center gap-1">
                            {((s.preauth_coe_received_at || s.application_stage === 'coe_sent') && (s.preauth_ea_signed_at || s.ea_signed_at || s.ea_status === 'signed')) && (
                              <Button
                                size="sm"
                                onClick={() => (canSelfCompletePreAuth ? setSelfPreAuthStudent(s) : setPreAuthStudent(s))}
                                disabled={!!s.pre_auth_sent_at || !!s.pre_auth_self_completed_at}
                                className={`h-8 px-2.5 text-[11px] font-semibold shrink-0 ${
                                  (s.pre_auth_sent_at || s.pre_auth_self_completed_at)
                                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-200 cursor-default hover:bg-emerald-100'
                                    : canSelfCompletePreAuth
                                      ? 'bg-emerald-700 hover:bg-emerald-800 text-white border border-emerald-700 shadow-sm'
                                      : 'bg-blue-700 hover:bg-blue-800 text-white border border-blue-700 shadow-sm'
                                }`}
                                title={
                                  s.pre_auth_sent_at
                                    ? `Pre-Auth email sent ${(s.pre_auth_sent_at || '').slice(0, 10)}`
                                    : s.pre_auth_self_completed_at
                                      ? `Self-completed on ${(s.pre_auth_self_completed_at || '').slice(0, 10)}`
                                      : canSelfCompletePreAuth
                                        ? 'Self-complete the Pre-Authorization on behalf of the student (audit-logged)'
                                        : 'Send Pre-Authorization email + Declaration to the student'
                                }
                                data-testid={`pre-auth-btn-${s.id}`}
                              >
                                <ShieldCheck size={13} weight="bold" className="mr-1" />
                                {(s.pre_auth_sent_at || s.pre_auth_self_completed_at)
                                  ? 'Pre-Auth ✓'
                                  : canSelfCompletePreAuth ? 'Self-Complete' : 'Pre-Authorization'}
                              </Button>
                            )}
                            <Link to={`/recruiter/student/${s.id}`} data-testid={`edit-student-${s.id}`}>
                              <Button size="sm" variant="ghost" className="h-8 px-2 text-xs">
                                <FilePlus size={14} className="mr-1" /> Edit
                              </Button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {remaining > 0 && (
                <div className="flex items-center justify-center gap-3 py-4 border-t border-gray-100 bg-gray-50/60" data-testid={`load-more-row-${groupKey}`}>
                  <span className="text-[11.5px] text-gray-600 tabular-nums">
                    Showing <span className="font-semibold text-gray-900">{shown.length}</span> of <span className="font-semibold text-gray-900">{items.length}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => loadMore(groupKey)}
                    className="h-8 text-[11.5px] font-medium border-gray-300 text-gray-800 hover:bg-gray-100 rounded-md"
                    data-testid={`load-more-btn-${groupKey}`}
                  >
                    Load next {Math.min(remaining, PAGE_SIZE)}
                  </Button>
                </div>
              )}
            </div>
              );
            };
            return recGroupByIntake ? (
              <div className="p-4 sm:p-5">
                <IntakeAccordion
                  students={filteredStudents}
                  stages={Object.keys(PIPELINE_LABELS)}
                  stageMeta={(k) => ({ label: PIPELINE_LABELS[k] || k, dot: PIPELINE_DOTS[k] || 'bg-gray-400', className: PIPELINE_COLORS[k] || 'bg-gray-50 text-gray-600 border-gray-200' })}
                  storageKey="recruiter.students.openIntakes"
                  emptyMessage="No students match your filters."
                  renderGroup={(items, g) => renderTable(items, `intake-${g?.key || 'unassigned'}`)}
                  variant="editorial"
                />
              </div>
            ) : renderTable(filteredStudents, 'flat');
          })()}
        </section>

        {/* ────────────────────────────────────────────────────────────── */}
        {/* KPI row — now below the Students List                           */}
        {/* ────────────────────────────────────────────────────────────── */}
        {stats && (
          <section className="border-t border-gray-200" data-testid="recruiter-stats">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-200">
              <KpiTile icon={Student}        label="Total Students" value={stats.total_students} testId="kpi-total" firstCol />
              <KpiTile icon={ClockCountdown} label="Drafts"         value={stats.drafts}         testId="kpi-drafts" />
              <KpiTile icon={CheckCircle}    label="Submitted"      value={stats.submitted}      testId="kpi-submitted" />
              <KpiTile icon={TrendUp}        label="Conversion"     value={`${conversionPct}%`}   sub={`${students.filter(s => ['coe_sent', 'half_money_received', 'second_money_received'].includes(s.application_stage)).length} COEs sent`} testId="kpi-conversion" />
            </div>
            <div className="border-b border-gray-200" />
          </section>
        )}

        {/* ────────────────────────────────────────────────────────────── */}
        {/* Earnings Forecast (hidden for sub-agents and when admin disabled it) */}
        {/* ────────────────────────────────────────────────────────────── */}
        {!isSubAgent && forecast && !forecast.hidden && (forecast.total_expected > 0 || forecast.breakdown?.some(b => b.count > 0)) && (
          <ForecastCard forecast={forecast} />
        )}

        {/* ────────────────────────────────────────────────────────────── */}
        {/* Two-column: Commission tiers + Commissions summary             */}
        {/* ────────────────────────────────────────────────────────────── */}
        {!commissionsHidden && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {tiersInfo && !tiersInfo.hidden && (tiersInfo.tiers?.length > 0 || tiersInfo.flat_commission > 0) && (
              <TiersCard tiersInfo={tiersInfo} />
            )}
            {commData && !commData.hidden && (commData.total_earned > 0 || commData.total_pending > 0 || commData.commissions?.length > 0) && (
              <CommissionsCard commData={commData} />
            )}
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────── */}
        {/* Sub-Agents card — visible only to parent recruiters             */}
        {/* ────────────────────────────────────────────────────────────── */}
        {!isSubAgent && <SubAgentsCard />}
      </div>

      {/* Resolve missing-doc dialog */}
      <Dialog open={!!resolvingFlag} onOpenChange={(o) => { if (!o) { setResolvingFlag(null); setResolveNote(''); } }}>
        <DialogContent data-testid="resolve-flag-dialog">
          <DialogHeader>
            <DialogTitle>Mark missing document as resolved</DialogTitle>
          </DialogHeader>
          {resolvingFlag && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                <strong>Student:</strong> {resolvingFlag.student_name}<br />
                <strong>Missing:</strong> {resolvingFlag.doc_type}<br />
                <strong>College&rsquo;s note:</strong> <em>{resolvingFlag.note || '(none)'}</em>
              </p>
              <div>
                <label className="text-xs uppercase text-gray-500 font-medium">Note for the college (optional)</label>
                <Textarea rows={4} placeholder="e.g. Uploaded a clearer scan under 'Grade 12 marksheet'." value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} data-testid="resolve-note-input" />
              </div>
              <p className="text-xs text-gray-500">Make sure the missing document has already been uploaded on the student profile before clicking Resolve.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setResolvingFlag(null); setResolveNote(''); }}>Cancel</Button>
            <Button onClick={submitResolve} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="resolve-submit-btn">
              Mark resolved & notify college
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PreAuthorizationModal
        open={!!preAuthStudent}
        onClose={() => setPreAuthStudent(null)}
        student={preAuthStudent}
        onSent={({ sent_at, to }) => {
          // Flip the local row so the button locks immediately without a full refetch.
          setStudents(prev => prev.map(s => s.id === preAuthStudent?.id
            ? { ...s, pre_auth_sent_at: sent_at, pre_auth_sent_to: to }
            : s));
        }}
      />
      <SelfCompletePreAuthModal
        open={!!selfPreAuthStudent}
        onClose={() => setSelfPreAuthStudent(null)}
        student={selfPreAuthStudent}
        onCompleted={({ completed_at, file_id }) => {
          setStudents(prev => prev.map(s => s.id === selfPreAuthStudent?.id
            ? { ...s, pre_auth_self_completed_at: completed_at, pre_auth_signed_file_id: file_id }
            : s));
        }}
      />
    </div>
  );
}

/* --------------------------------------------------------------------------
   Sub-components
   -------------------------------------------------------------------------- */

function KpiTile({ icon: Icon, label, value, sub, testId, firstCol }) {
  return (
    <div className={`px-5 py-6 ${firstCol ? 'pl-0' : ''}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={13} className="text-gray-400" />
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">{label}</p>
      </div>
      <p className="text-3xl font-semibold text-gray-900 tabular-nums tracking-tight leading-none" style={{ fontFamily: "'Libre Baskerville', serif" }}>{value}</p>
      {sub && <p className="text-[10px] text-gray-500 mt-2">{sub}</p>}
    </div>
  );
}

function StagePill({ active, onClick, label, count, dot, tone, testId }) {
  const base = 'inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold rounded-full border transition-colors whitespace-nowrap';
  const activeCls = 'bg-gray-900 text-white border-gray-900';
  const idleCls = 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50';
  return (
    <button type="button" className={`${base} ${active ? activeCls : idleCls}`} onClick={onClick} data-testid={testId}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white' : dot}`} />}
      {label}
      <span className={`inline-flex items-center justify-center min-w-[18px] px-1 text-[10px] font-bold rounded-full ${active ? 'bg-white/20 text-white' : (tone === 'gray' ? 'bg-gray-100 text-gray-600' : 'bg-gray-100 text-gray-500')}`}>
        {count}
      </span>
    </button>
  );
}

/* Earnings Forecast — with a horizontal funnel viz above the table */
function ForecastCard({ forecast }) {
  const labels = { enrolled: 'Enrolled', admitted: 'Admitted', applied: 'Applied', submitted: 'Submitted', draft: 'Draft' };
  const colors = {
    enrolled: 'bg-emerald-500',
    admitted: 'bg-green-500',
    applied: 'bg-indigo-500',
    submitted: 'bg-blue-500',
    draft: 'bg-yellow-500',
  };
  const rows = forecast.breakdown || [];
  const maxCount = Math.max(1, ...rows.map(r => r.count || 0));
  return (
    <section className="bg-white border border-gray-200" data-testid="earnings-forecast-card">
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">Forecast</p>
          <h2 className="text-lg font-semibold text-gray-900 mt-1" style={{ fontFamily: "'Libre Baskerville', serif" }}>Earnings Forecast</h2>
          <p className="text-[11px] text-gray-500 mt-1 max-w-md">
            Probability-weighted estimate across your active pipeline. Numbers move as your students progress through stages.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 mb-8 border border-gray-200 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
          <div className="p-5" data-testid="forecast-expected">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">Expected</p>
            <p className="text-3xl font-semibold text-gray-900 tabular-nums mt-1.5" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              ${Number(forecast.total_expected || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
            <p className="text-[11px] text-gray-500 mt-1">across all non-rejected students</p>
          </div>
          <div className="p-5" data-testid="forecast-locked">
            <div className="flex items-center gap-1.5">
              <Lock size={11} className="text-gray-500" weight="fill" />
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">Locked In</p>
            </div>
            <p className="text-3xl font-semibold text-gray-900 tabular-nums mt-1.5" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              ${Number(forecast.locked_in || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
            <p className="text-[11px] text-gray-500 mt-1">enrolled — 100% confirmed</p>
          </div>
        </div>

        {/* Funnel bars */}
        {rows.length > 0 && (
          <div className="space-y-3">
            {rows.map(b => {
              const pct = maxCount === 0 ? 0 : Math.round(((b.count || 0) / maxCount) * 100);
              const muted = (b.count || 0) === 0;
              return (
                <div key={b.stage} className={`flex items-center gap-4 ${muted ? 'opacity-40' : ''}`} data-testid={`forecast-stage-${b.stage}`}>
                  <div className="w-28 shrink-0 flex items-center gap-2">
                    <span aria-hidden className={`inline-block w-1.5 h-1.5 rounded-full ${colors[b.stage] || 'bg-gray-400'}`} />
                    <span className="text-[11px] font-medium text-gray-700">{labels[b.stage] || b.stage}</span>
                  </div>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gray-900 rounded-full transition-all" style={{ width: `${Math.max(pct, 2)}%` }} />
                  </div>
                  <div className="w-40 flex items-center justify-between shrink-0 gap-3">
                    <span className="text-[11px] text-gray-500 tabular-nums">{b.count} · {Math.round((b.weight || 0) * 100)}%</span>
                    <span className="text-[13px] font-semibold text-gray-900 tabular-nums">${Number(b.expected || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function TiersCard({ tiersInfo }) {
  return (
    <section className="bg-white border border-gray-200 overflow-hidden" data-testid="recruiter-tiers-card">
      <div className="px-6 py-5 border-b border-gray-200">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">Earnings</p>
        <h2 className="text-lg font-semibold text-gray-900 mt-1" style={{ fontFamily: "'Libre Baskerville', serif" }}>Commission Tiers</h2>
        <p className="text-[11px] text-gray-500 mt-1">The higher the sale price you negotiate, the higher your commission.</p>
      </div>
      {tiersInfo.tiers?.length > 0 ? (
        <ul className="divide-y divide-gray-200">
          {tiersInfo.tiers.map((t, i) => (
            <li key={i} className="flex items-center justify-between px-6 py-3.5" data-testid={`my-tier-row-${i}`}>
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-md border border-gray-200 bg-gray-50 text-gray-700 flex items-center justify-center text-[11px] font-semibold tabular-nums">{i + 1}</div>
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-gray-500">When Sale Price ≥</p>
                  <p className="text-[13px] font-semibold text-gray-900 tabular-nums">${Number(t.sale_price).toLocaleString()}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-gray-500">You Earn</p>
                <p className="text-[13px] font-semibold text-gray-900 tabular-nums">${Number(t.commission_amount).toLocaleString()}</p>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="px-6 py-6 text-[13px] text-gray-700">
          Flat commission per student: <span className="font-semibold text-gray-900 tabular-nums">${Number(tiersInfo.flat_commission).toLocaleString()}</span>
        </div>
      )}
    </section>
  );
}

function CommissionsCard({ commData }) {
  return (
    <section className="bg-white border border-gray-200 overflow-hidden" data-testid="recruiter-commissions">
      <div className="px-6 py-5 border-b border-gray-200">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">Earnings</p>
        <h2 className="text-lg font-semibold text-gray-900 mt-1" style={{ fontFamily: "'Libre Baskerville', serif" }}>My Payouts</h2>
      </div>
      <div className="grid grid-cols-2 divide-x divide-gray-200">
        <div className="p-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">Earned</p>
          <p className="text-3xl font-semibold text-gray-900 tabular-nums mt-1" style={{ fontFamily: "'Libre Baskerville', serif" }}>${commData.total_earned.toLocaleString()}</p>
        </div>
        <div className="p-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">Pending</p>
          <p className="text-3xl font-semibold text-gray-900 tabular-nums mt-1" style={{ fontFamily: "'Libre Baskerville', serif" }}>${commData.total_pending.toLocaleString()}</p>
        </div>
      </div>
      {commData.commissions?.length > 0 && (
        <>
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">Recent</p>
            <span className="text-[10px] text-gray-400">{commData.commissions.length} total</span>
          </div>
          <ul className="divide-y divide-gray-200 max-h-64 overflow-y-auto">
            {commData.commissions.slice(0, 6).map(c => (
              <li key={c.id} className="px-6 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-gray-900 truncate">{c.student_name || '—'}</p>
                  <p className="text-[11px] text-gray-500 truncate">{c.description || new Date(c.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[13px] font-semibold text-gray-900 tabular-nums">${c.amount?.toLocaleString()}</p>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-medium text-gray-600`}>
                    <span aria-hidden className={`inline-block w-1.5 h-1.5 rounded-full ${c.status === 'paid' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                    {c.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
